import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";
import {
    MapPin,
    Loader2,
    UserPlus,
    Sparkles,
    Clock,
    ChevronRight,
    LogIn,
    ImageOff
} from "lucide-react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const MainHome = () => {
    const navigate = useNavigate();
    const { user } = useUserAuth();
    const [userName, setUserName] = useState("");
    const [loading, setLoading] = useState(true);
    const [sliderImages, setSliderImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(true);

    // Fetch user data
    useEffect(() => {
        const fetchUserData = async () => {
            if (!user) {
                setLoading(false);
                return;
            }
            try {
                const firestore = getFirestore();
                const docRef = doc(firestore, "Users", user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const userData = docSnap.data();
                    setUserName(userData.nombre || "");
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [user]);

    // Fetch header images from Firebase Storage
    useEffect(() => {
        const fetchHeaderImages = async () => {
            try {
                const headerRef = ref(storage, "header");
                const result = await listAll(headerRef);

                const urlPromises = result.items.map(imageRef => getDownloadURL(imageRef));
                const urls = await Promise.all(urlPromises);

                setSliderImages(urls);
            } catch (error) {
                console.error("Error fetching header images:", error);
                setSliderImages([]);
            } finally {
                setLoadingImages(false);
            }
        };
        fetchHeaderImages();
    }, []);

    // Slider configuration
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        arrows: false,
        adaptiveHeight: false,
        pauseOnHover: true,
        dotsClass: "slick-dots custom-dots",
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="bg-[#FF0000] p-5 rounded-3xl shadow-lg">
                        <Loader2 className="w-12 h-12 animate-spin text-white" strokeWidth={2.5} />
                    </div>
                    <p className="text-gray-800 font-bold text-base">
                        Cargando...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
                {/* Welcome Card for Logged-in Users */}
                {user && (
                    <div className="bg-[#FF0000] rounded-3xl shadow-lg p-6 text-white">
                        <div className="flex items-center gap-4 mb-3">
                            <div className="bg-white/20 p-3 rounded-2xl">
                                <Sparkles className="w-7 h-7" strokeWidth={2.5} />
                            </div>
                            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                                ¡Hola{userName ? `, ${userName}` : ""}!
                            </h1>
                        </div>
                        <p className="text-base text-white/90">
                            Descubre nuestras promociones y encuentra tu sucursal más cercana.
                        </p>
                    </div>
                )}

                {/* Slider Section */}
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900 px-2">
                        Promociones
                    </h2>

                    {loadingImages ? (
                        <div className="bg-white rounded-3xl shadow-md aspect-[7/9] flex items-center justify-center">
                            <div className="text-center space-y-3">
                                <div className="bg-[#FF0000] p-4 rounded-2xl inline-block">
                                    <Loader2 className="w-10 h-10 animate-spin text-white" strokeWidth={2.5} />
                                </div>
                                <p className="text-gray-600 font-medium">Cargando promociones...</p>
                            </div>
                        </div>
                    ) : sliderImages.length > 0 ? (
                        <div className="bg-gray-200 rounded-3xl shadow-md overflow-hidden">
                            <style>
                                {`
                                    .custom-dots {
                                        bottom: 15px;
                                        padding: 0;
                                    }
                                    .custom-dots li {
                                        margin: 0 4px;
                                    }
                                    .custom-dots li button:before {
                                        font-family: 'slick';
                                        font-size: 10px;
                                        line-height: 20px;
                                        color: white;
                                        opacity: 0.5;
                                        transition: opacity 0.2s ease;
                                    }
                                    .custom-dots li.slick-active button:before {
                                        opacity: 1;
                                        color: white;
                                    }
                                    .slick-slide { outline: none; }
                                    .slick-slide > div { outline: none; }
                                `}
                            </style>
                            <Slider {...sliderSettings}>
                                {sliderImages.map((image, index) => (
                                    <div key={`slide-${index}`}>
                                        <div className="relative">
                                            <img
                                                src={image}
                                                alt={`Promoción ${index + 1}`}
                                                className="w-full aspect-[7/9] object-cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "https://placehold.co/900x700/FF0000/FFFFFF?text=Error+al+cargar";
                                                }}
                                            />
                                            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/50 to-transparent pointer-events-none"></div>
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    ) : (
                        <div className="bg-white rounded-3xl shadow-md aspect-[9/7] flex items-center justify-center text-center p-4">
                            <div className="space-y-3">
                                <div className="bg-gray-100 p-4 rounded-2xl inline-block">
                                    <ImageOff className="w-10 h-10 text-gray-400" strokeWidth={2} />
                                </div>
                                <div>
                                    <p className="font-bold text-gray-800">Sin promociones</p>
                                    <p className="text-gray-500 text-sm">Vuelve a consultar más tarde.</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Order and Pickup Button */}
                <button
                    onClick={() => navigate("/sucursales")}
                    className="bg-[#FF0000] text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center gap-2.5 transform hover:scale-[1.02] w-full"
                >
                    <MapPin className="w-6 h-6" strokeWidth={2.5} />
                    ¡Ordena y Recoge!
                </button>

                {/* Welcome Card for Guests */}
                {!user && (
                    <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100">
                        <div className="flex items-center gap-4 mb-5">
                            <div className="bg-[#FF0000] p-3.5 rounded-2xl flex-shrink-0">
                                <UserPlus className="w-7 h-7 text-white" strokeWidth={2.5} />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                                    ¡Bienvenido a Pollo Feliz!
                                </h2>
                                <p className="text-sm text-gray-600">
                                    Crea una cuenta para disfrutar de beneficios exclusivos.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                onClick={() => navigate("/signup")}
                                className="bg-[#FF0000] text-white py-3.5 px-4 rounded-xl font-bold text-base hover:shadow-md transition-all flex items-center justify-center gap-2.5 transform hover:scale-[1.02]"
                            >
                                <UserPlus className="w-5 h-5" strokeWidth={2.5} />
                                Crear cuenta
                            </button>
                            <button
                                onClick={() => navigate("/login")}
                                className="bg-gray-100 text-gray-800 py-3.5 px-4 rounded-xl font-bold text-base hover:bg-gray-200 transition-all flex items-center justify-center gap-2.5"
                            >
                                <LogIn className="w-5 h-5" strokeWidth={2.5} />
                                Iniciar sesión
                            </button>
                        </div>
                    </div>
                )}

                {/* Quick Actions Grid */}
                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => navigate("/sucursales")}
                        className="bg-white rounded-3xl shadow-md p-4 transition-all hover:shadow-lg hover:scale-105 group text-center flex flex-col justify-between items-center aspect-square"
                    >
                        <div className="flex-1 flex flex-col justify-center items-center gap-3">
                            <div className="bg-[#FF0000] p-4 rounded-2xl group-hover:scale-110 transition-transform">
                                <MapPin className="w-8 h-8 text-white" strokeWidth={2.5} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">
                                Sucursales
                            </h3>
                        </div>
                        <div className="flex items-center text-[#FF0000] text-sm font-bold">
                            Ver mapa
                            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
                        </div>
                    </button>

                    <div className="bg-[#FF0000] rounded-3xl shadow-md p-4 text-white text-center flex flex-col justify-center items-center aspect-square">
                        <div className="bg-white/20 p-4 rounded-2xl mb-3">
                            <Clock className="w-8 h-8" strokeWidth={2.5} />
                        </div>
                        <h3 className="text-lg font-bold">
                            Horario
                        </h3>
                        <p className="text-white/90 text-sm mb-1">
                            Abierto todos los días
                        </p>
                        <p className="text-base font-bold bg-white/20 px-3 py-1 rounded-full">
                            10 AM - 5 PM
                        </p>
                    </div>
                </div>

                {/* App Features - Only for guests */}
                {!user && (
                    <div className="bg-white rounded-3xl shadow-md p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                            ¿Por qué crear una cuenta?
                        </h3>
                        <div className="space-y-4">
                            {[
                                { icon: Sparkles, color: "green", title: "Promociones exclusivas", subtitle: "Accede a ofertas y descuentos especiales." },
                                { icon: Clock, color: "blue", title: "Pedidos más rápidos", subtitle: "Guarda tus sucursales y preferencias." },
                                { icon: MapPin, color: "purple", title: "Gestiona tus favoritos", subtitle: "Acceso rápido a tus ubicaciones preferidas." }
                            ].map((item, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className={`bg-${item.color}-100 p-2.5 rounded-xl flex-shrink-0`}>
                                        <item.icon className={`w-6 h-6 text-${item.color}-600`} strokeWidth={2} />
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold text-gray-800">{item.title}</p>
                                        <p className="text-sm text-gray-600">{item.subtitle}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="text-center pt-8 pb-4">
                    <p className="text-gray-500 text-sm font-medium">
                        © {new Date().getFullYear()} Pollo Feliz • Todos los derechos reservados
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MainHome;